import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../lib/api";
import { useAuth } from "../stores/auth";
import { usePrefs, Theme, Lang } from "../stores/prefs";
import { useQueue } from "../stores/queue";

import { TopBar } from "../components/ui/TopBar";
import { Icon } from "../components/ui/Icon";
import { Row } from "../components/ui/Row";
import { Toggle } from "../components/ui/Toggle";
import { Seg } from "../components/ui/Seg";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { CatalogAdminFlow } from "../components/catalog/CatalogAdminFlow";
import { IncrementalSyncModal } from "../components/catalog/IncrementalSyncModal";
import { CategoriesAdmin } from "../components/catalog/CategoriesAdmin";
import { StockOutReasonsAdmin } from "../components/stock/StockOutReasonsAdmin";
import { UsersAdmin } from "../components/operators/UsersAdmin";
import { LocationsAdmin } from "../components/operators/LocationsAdmin";
import { usePermissions } from "../stores/permissions";

type CatalogStatus = {
  total_products: number;
  shopify_matched: number;
  shopify_missing: number;
  odoo_linked: number;
  odoo_missing: number;
  last_synced_at: string | null;
};

type CatalogStep = "reset" | "shopify" | "odoo";

type Health = {
  status: string;
  uptime_seconds: number;
  version: string;
  db: string;
  tiktok: string;
  shopify: string;
  odoo: string;
  telegram?: string;
};

const THEME_OPTIONS = ["Auto", "Light", "Dark"] as const;
const LANG_OPTIONS = ["IT", "EN"] as const;

const API_BASE = import.meta.env.VITE_API_URL || "https://inv-api.tcgeurope.it";

export function Settings() {
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const prefs = usePrefs((s) => s.prefs);
  const setPref = usePrefs((s) => s.set);
  const queue = useQueue((s) => s.pending);
  const qc = useQueryClient();
  const [cacheStorageMb, setCacheStorageMb] = useState<number | null>(null);
  const isAdmin = user?.role === "admin";
  const { hasPermission } = usePermissions();
  const canManageLocations = hasPermission("location_manage");
  const [catalogFlow, setCatalogFlow] = useState<CatalogStep | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState<null | "labels" | "quaderno">(null);

  const { data: health } = useQuery<Health>({
    queryKey: ["health", "deep"],
    queryFn: async () => (await api.get("/api/health", { params: { deep: true } })).data,
    refetchInterval: 60_000,
  });

  const { data: catalogStatus } = useQuery<CatalogStatus>({
    queryKey: ["catalog-status"],
    queryFn: async () => (await api.get("/api/catalog/status")).data,
    refetchInterval: 60_000,
    enabled: isAdmin,
  });

  const { data: labelCats } = useQuery<{ id: number; name: string; product_count: number }[]>({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/api/categories")).data,
    enabled: labelsOpen,
  });

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.storage?.estimate) return;
    navigator.storage
      .estimate()
      .then((est) => {
        if (typeof est.usage === "number") {
          setCacheStorageMb(est.usage / (1024 * 1024));
        }
      })
      .catch(() => {});
  }, []);

  function tiktokConnect() {
    window.location.href = `${API_BASE}/api/auth/tiktok/start`;
  }

  async function clearCache() {
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      qc.clear();
      setCacheStorageMb(0);
    } catch {
      /* noop */
    }
  }

  async function downloadPdf(
    kind: "labels" | "quaderno",
    path: string,
    filename: string,
    params?: Record<string, unknown>,
  ) {
    if (pdfBusy) return;
    setPdfBusy(kind);
    try {
      const res = await api.get(path, { responseType: "blob", params });
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Generazione PDF fallita: ${errMsg(e)}`);
    } finally {
      setPdfBusy(null);
    }
  }

  const userInitials = (() => {
    const src = (user?.display_name || user?.username || "FB").trim();
    const parts = src.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  return (
    <>
      <TopBar back onBack={() => nav(-1)} title="Impostazioni" sub={`Account & app · v${health?.version || "2.1.0"}`} />

      <div style={{ padding: "14px 18px 0" }}>
        <div
          style={{
            background: "var(--tcg-navy)",
            color: "var(--tcg-cream)",
            borderRadius: 20,
            padding: "16px 16px 14px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <img
            src="/assets/star.png"
            alt=""
            style={{
              position: "absolute",
              bottom: -22,
              right: -16,
              width: 88,
              opacity: 0.7,
              transform: "rotate(-12deg)",
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "var(--tcg-yellow)",
                color: "var(--tcg-navy-deep)",
                fontSize: 20,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "inset 0 -2px 0 var(--tcg-yellow-deep)",
              }}
            >
              {userInitials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: "-0.01em" }}>
                {user?.display_name || user?.username}
              </div>
              <div
                className="tcg-mono"
                style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", fontWeight: 700, marginTop: 2 }}
              >
                {user?.email || `@${user?.username}`}
              </div>
              <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
                <span
                  style={{
                    background: "var(--tcg-yellow)",
                    color: "var(--tcg-navy-deep)",
                    fontSize: 9,
                    fontWeight: 900,
                    padding: "3px 7px",
                    borderRadius: 5,
                    letterSpacing: "0.04em",
                  }}
                >
                  {(user?.role || "operator").toUpperCase()}
                </span>
                <span
                  style={{
                    background: "rgba(255,255,255,0.16)",
                    fontSize: 9,
                    fontWeight: 800,
                    padding: "3px 7px",
                    borderRadius: 5,
                    letterSpacing: "0.04em",
                  }}
                >
                  SEDE VICENZA
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SettingsGroup title="Account">
        <Row icon="user" iconBg="#EEF2FF" iconFg="var(--tcg-navy)" label="Profilo" sub={user?.email || `@${user?.username}`} right={<Icon name="chev-r" />} />
        <Row icon="lock" iconBg="#FFF5C2" iconFg="#8A6B00" label="Cambia password" sub="Richiedila a un admin via Telegram" right={<Icon name="chev-r" />} />
        <Row icon="pin" iconBg="#E0F2FE" iconFg="var(--tcg-info)" label="Sede di default" sub="Vicenza · Sede principale" right={<><span style={{ fontSize: 11, fontWeight: 700, color: "var(--tcg-ink-soft)" }}>Vicenza</span><Icon name="chev-r" /></>} />
        <Row icon="box" iconBg="#FCE7F3" iconFg="#9D174D" label="Sessione corrente" sub={`uptime ${Math.floor((health?.uptime_seconds ?? 0) / 60)}m · ${health?.version || "—"}`} right={<Icon name="chev-r" />} last />
      </SettingsGroup>

      <SettingsGroup title="Notifiche">
        <Row
          icon="bell"
          iconBg="#FEF3C7"
          iconFg="var(--tcg-warn)"
          label="Push notifiche"
          sub="Eventi importanti dell'app"
          right={<Toggle on={prefs.push_enabled} onChange={(v) => setPref("push_enabled", v)} />}
        />
        <Row
          icon="flag"
          iconBg="var(--tcg-danger-bg)"
          iconFg="var(--tcg-danger)"
          label="Avvisi sotto soglia"
          sub={`Notifica quando SKU < ${prefs.low_stock_threshold} pezzi`}
          right={<Toggle on={prefs.low_stock_alert} onChange={(v) => setPref("low_stock_alert", v)} />}
        />
        <Row
          icon="truck"
          iconBg="#EEF2FF"
          iconFg="#4338CA"
          label="Eventi transfer"
          sub="Apertura, chiusura, mancata quadratura"
          right={<Toggle on={prefs.transfer_alerts} onChange={(v) => setPref("transfer_alerts", v)} />}
          last
        />
      </SettingsGroup>

      <SettingsGroup
        title="Operatività"
        footer="La soglia minima si applica a tutti i prodotti senza override specifico nella scheda SKU."
      >
        <Row
          icon="chart"
          iconBg="#FFF5C2"
          iconFg="#8A6B00"
          label="Soglia minima stock"
          sub={`Sotto ${prefs.low_stock_threshold} pezzi → warning rosso`}
          right={
            <div
              className="tcg-num"
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: "var(--tcg-navy)",
                minWidth: 28,
                textAlign: "right",
              }}
            >
              {prefs.low_stock_threshold}
            </div>
          }
        />
        <div style={{ padding: "0 14px 12px" }}>
          <input
            type="range"
            min={1}
            max={20}
            value={prefs.low_stock_threshold}
            onChange={(e) => setPref("low_stock_threshold", parseInt(e.target.value, 10))}
            style={{ width: "100%", accentColor: "var(--tcg-navy)" }}
          />
          <div
            className="tcg-mono"
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 9,
              color: "var(--tcg-muted)",
              fontWeight: 700,
              marginTop: 2,
            }}
          >
            <span>1</span>
            <span>5</span>
            <span>10</span>
            <span>15</span>
            <span>20</span>
          </div>
        </div>
        <Row
          icon="check"
          iconBg="#FCE7F3"
          iconFg="#9D174D"
          label="Doppio tap su Stock Out"
          sub="Conferma extra per evitare uscite accidentali"
          right={<Toggle on={prefs.double_tap_stock_out} onChange={(v) => setPref("double_tap_stock_out", v)} />}
          last
        />
      </SettingsGroup>

      <SettingsGroup title="Scanner & camera">
        <Row
          icon="scan"
          iconBg="#EEF2FF"
          iconFg="var(--tcg-navy)"
          label="Beep al riconoscimento"
          sub="Tono breve sintetizzato (Web Audio)"
          right={<Toggle on={prefs.beep} onChange={(v) => setPref("beep", v)} />}
        />
        <Row
          icon="bolt"
          iconBg="#FCE7F3"
          iconFg="#9D174D"
          label="Vibrazione"
          sub="Feedback aptico su scan"
          right={<Toggle on={prefs.vibrate} onChange={(v) => setPref("vibrate", v)} />}
        />
        <Row
          icon="sync"
          iconBg="#E0F2FE"
          iconFg="var(--tcg-info)"
          label="Autofocus continuo"
          sub="Consuma più batteria"
          right={<Toggle on={prefs.autofocus_camera} onChange={(v) => setPref("autofocus_camera", v)} />}
          last
        />
      </SettingsGroup>

      <SettingsGroup title="Integrazioni">
        <Row
          icon="tiktok"
          iconBg="#FCE7F3"
          iconFg="#9D174D"
          label="TikTok Shop"
          sub={health?.tiktok === "ok" ? "Connesso · sync via webhook" : "Non connesso"}
          right={
            health?.tiktok === "ok" ? (
              <Badge tone="success">CONNESSO</Badge>
            ) : (
              <Button variant="primary" size="small" onClick={tiktokConnect}>
                Connect
              </Button>
            )
          }
        />
        <Row
          icon="sync"
          iconBg="#E0F2FE"
          iconFg="var(--tcg-info)"
          label="Sync automatico"
          sub="Polling background ogni 30 minuti"
          right={<Toggle on={prefs.auto_sync} onChange={(v) => setPref("auto_sync", v)} />}
        />
        <Row
          icon="shopify"
          iconBg="#CCFBF1"
          iconFg="#0F766E"
          label="Shopify"
          sub={statusLabel(health?.shopify)}
          right={<Badge tone={statusTone(health?.shopify)}>{(health?.shopify || "—").toUpperCase()}</Badge>}
        />
        <Row
          icon="box"
          iconBg="#FFF5C2"
          iconFg="#8A6B00"
          label="Odoo"
          sub={statusLabel(health?.odoo)}
          right={<Badge tone={statusTone(health?.odoo)}>{(health?.odoo || "—").toUpperCase()}</Badge>}
        />
        <Row
          icon="bell"
          iconBg="#EEF2FF"
          iconFg="var(--tcg-navy)"
          label="Telegram"
          sub={statusLabel(health?.telegram)}
          right={<Badge tone={statusTone(health?.telegram)}>{(health?.telegram || "—").toUpperCase()}</Badge>}
          last
        />
      </SettingsGroup>

      {isAdmin && (
        <SettingsGroup
          title="Amministrazione catalogo"
          footer="TikTok Shop è la fonte unica del catalogo prodotti. Shopify e Odoo sono allineati a posteriori riscrivendo lo SKU."
        >
          <Row
            icon="sync"
            iconBg="var(--tcg-success-bg)"
            iconFg="var(--tcg-success)"
            label="Stato sincronizzazione"
            sub={
              catalogStatus
                ? `${catalogStatus.total_products} prodotti · ${catalogStatus.shopify_matched} Shopify · ${catalogStatus.odoo_linked} Odoo`
                : "—"
            }
            right={
              <span className="tcg-mono" style={{ fontSize: 10, color: "var(--tcg-muted)" }}>
                {catalogStatus?.last_synced_at
                  ? new Date(catalogStatus.last_synced_at).toLocaleString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "mai"}
              </span>
            }
          />
          {catalogStatus && catalogStatus.shopify_missing > 0 && (
            <Row
              icon="info"
              iconBg="var(--tcg-warn-bg)"
              iconFg="var(--tcg-warn)"
              label="Prodotti senza match Shopify"
              sub="Richiede riallineamento o creazione manuale"
              right={<Badge tone="warn">{catalogStatus.shopify_missing}</Badge>}
            />
          )}
          <div style={{ padding: "10px 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            <Button variant="primary" onClick={() => setSyncOpen(true)}>
              <Icon name="sync" size={14} /> Sincronizza catalogo (additivo)
            </Button>
            <Button variant="primary" onClick={() => setCatalogFlow("shopify")}>
              <Icon name="shopify" size={14} /> Forza riallineamento Shopify
            </Button>
            <Button variant="primary" onClick={() => setCatalogFlow("odoo")}>
              <Icon name="box" size={14} /> Forza sync Odoo
            </Button>
            <Button variant="danger" onClick={() => setCatalogFlow("reset")}>
              <Icon name="flag" size={14} /> DANGER · Reset catalogo da TikTok
            </Button>
          </div>
        </SettingsGroup>
      )}

      {isAdmin && (
        <SettingsGroup
          title="Utenti"
          footer="Solo gli amministratori possono gestire gli operatori. Le password generate sono mostrate una sola volta."
        >
          <Row
            icon="user"
            iconBg="#EEF2FF"
            iconFg="var(--tcg-navy)"
            label="Gestione operatori"
            sub="Crea utenti, cambia ruolo, reset password"
            right={<Icon name="chev-r" />}
            onClick={() => setUsersOpen(true)}
            last
          />
        </SettingsGroup>
      )}

      {canManageLocations && (
        <SettingsGroup
          title="Sedi"
          footer="Le sedi vending (macchinette) attive compaiono come destinazioni in Transfer Out. Una sede collegata a un viaggio aperto viene disattivata, non eliminata."
        >
          <Row
            icon="pin"
            iconBg="#E0F2FE"
            iconFg="var(--tcg-info)"
            label="Gestione sedi"
            sub="Crea, rinomina, cambia tipo, attiva/disattiva ed elimina"
            right={<Icon name="chev-r" />}
            onClick={() => setLocationsOpen(true)}
            last
          />
        </SettingsGroup>
      )}

      {isAdmin && (
        <SettingsGroup
          title="Motivi di uscita"
          footer="I 7 motivi di default non sono eliminabili (solo disattivabili). I motivi custom possono essere aggiunti, disattivati o eliminati."
        >
          <Row
            icon="out"
            iconBg="var(--tcg-danger-bg)"
            iconFg="var(--tcg-danger)"
            label="Motivi di Stock Out"
            sub="Attiva/disattiva e aggiungi motivi custom"
            right={<Icon name="chev-r" />}
            onClick={() => setReasonsOpen(true)}
            last
          />
        </SettingsGroup>
      )}

      {isAdmin && (
        <SettingsGroup
          title="Categorie"
          footer="Le categorie non manuali vengono riassegnate automaticamente ad ogni import/reset del catalogo."
        >
          <Row
            icon="tag"
            iconBg="#E6EBF8"
            iconFg="var(--tcg-navy)"
            label="Categorie prodotto"
            sub="Crea, rinomina, elimina e assegna prodotti"
            right={<Icon name="chev-r" />}
            onClick={() => setCategoriesOpen(true)}
            last
          />
        </SettingsGroup>
      )}

      <SettingsGroup title="Aspetto & lingua">
        <Row
          icon="star"
          iconBg="#EEF2FF"
          iconFg="var(--tcg-navy)"
          label="Tema"
          right={
            <Seg<Theme>
              options={THEME_OPTIONS}
              value={prefs.theme}
              onChange={(v) => setPref("theme", v)}
            />
          }
        />
        <Row
          icon="flag"
          iconBg="#FFF5C2"
          iconFg="#8A6B00"
          label="Lingua"
          right={
            <Seg<Lang>
              options={LANG_OPTIONS}
              value={prefs.lang}
              onChange={(v) => setPref("lang", v)}
            />
          }
          last
        />
      </SettingsGroup>

      <SettingsGroup title="PDF & stampabili">
        <Row
          icon="note"
          iconBg="#F0FDF4"
          iconFg="var(--tcg-success)"
          label="Quaderno QR sacchetti"
          sub="TRP-A-01 … per i viaggi cartacei"
          right={<Icon name={pdfBusy === "quaderno" ? "sync" : "arrow-r"} className={pdfBusy === "quaderno" ? "tcg-spin" : undefined} />}
          onClick={() => downloadPdf("quaderno", "/api/qr-quaderno-pdf?type=transfer", "quaderno-qr-sacchetti.pdf")}
        />
        <Row
          icon="tag"
          iconBg="#E6EBF8"
          iconFg="var(--tcg-navy)"
          label="Etichette barcode prodotti"
          sub="PDF stampabile · tutte o una categoria"
          right={<Icon name={pdfBusy === "labels" ? "sync" : "arrow-r"} className={pdfBusy === "labels" ? "tcg-spin" : undefined} />}
          onClick={() => setLabelsOpen(true)}
          last
        />
      </SettingsGroup>

      <SettingsGroup title="Dati locali">
        <Row
          icon="box"
          iconBg="var(--tcg-bg)"
          iconFg="var(--tcg-ink-soft)"
          label="Cache app"
          sub={cacheStorageMb !== null ? `Storage browser · ${cacheStorageMb.toFixed(1)} MB` : "Calcolando…"}
          right={
            <button
              type="button"
              className="tcg-btn tcg-btn-ghost tcg-btn-small"
              style={{ background: "var(--tcg-bg)", border: "1px solid var(--tcg-border)", fontSize: 11 }}
              onClick={clearCache}
            >
              Svuota
            </button>
          }
        />
        <Row
          icon="note"
          iconBg="#F0FDF4"
          iconFg="var(--tcg-success)"
          label="Coda offline"
          sub={
            queue.length === 0
              ? "Nessun evento in attesa"
              : `${queue.length} eventi in attesa di sync`
          }
          right={
            queue.length === 0 ? (
              <Badge tone="success">SINCRONIZZATA</Badge>
            ) : (
              <Badge tone="warn">{queue.length} PENDING</Badge>
            )
          }
          last
        />
      </SettingsGroup>

      <SettingsGroup title="Pericolo">
        <Row
          icon="out"
          iconBg="var(--tcg-danger-bg)"
          iconFg="var(--tcg-danger)"
          label="Esci dal dispositivo"
          sub={`${user?.username} · questa sessione`}
          right={<Icon name="chev-r" />}
          danger
          last
          onClick={() => {
            logout();
            nav("/login", { replace: true });
          }}
        />
      </SettingsGroup>

      <div style={{ padding: "20px 22px 8px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, opacity: 0.8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "var(--tcg-yellow)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 3,
            }}
          >
            <img src="/assets/tcg-card-mark.png" alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "var(--tcg-ink)" }}>TCG Inventory Hub</div>
            <div className="tcg-mono" style={{ fontSize: 9, color: "var(--tcg-muted)", fontWeight: 600 }}>
              v{health?.version || "2.1.0"} · prod
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 10, fontWeight: 500 }}>
          © 2026 TCG Europe · FGC Group SRL
        </div>
      </div>

      <div style={{ height: 32 }} />

      {catalogFlow && (
        <CatalogAdminFlow
          open={catalogFlow !== null}
          initialStep={catalogFlow}
          onClose={() => setCatalogFlow(null)}
        />
      )}

      {isAdmin && <IncrementalSyncModal open={syncOpen} onClose={() => setSyncOpen(false)} />}

      {isAdmin && (
        <UsersAdmin
          open={usersOpen}
          onClose={() => setUsersOpen(false)}
          currentUsername={user?.username || ""}
        />
      )}

      {canManageLocations && (
        <LocationsAdmin open={locationsOpen} onClose={() => setLocationsOpen(false)} />
      )}

      {isAdmin && <CategoriesAdmin open={categoriesOpen} onClose={() => setCategoriesOpen(false)} />}

      {isAdmin && <StockOutReasonsAdmin open={reasonsOpen} onClose={() => setReasonsOpen(false)} />}

      <Modal open={labelsOpen} onClose={() => setLabelsOpen(false)} title="Etichette barcode">
        <div style={{ fontSize: 12, color: "var(--tcg-muted)", marginBottom: 12, fontWeight: 600 }}>
          Scegli quali prodotti includere nel PDF.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Button
            variant="primary"
            block
            disabled={pdfBusy === "labels"}
            onClick={() => {
              setLabelsOpen(false);
              downloadPdf("labels", "/api/products/qr-pdf/print", "labels-tutte.pdf");
            }}
          >
            <Icon name="tag" size={14} /> Tutte le categorie
          </Button>
          <Button
            variant="primary"
            block
            disabled={pdfBusy === "labels"}
            onClick={() => {
              setLabelsOpen(false);
              downloadPdf("labels", "/api/products/qr-pdf/by-category", "labels-per-categoria.pdf");
            }}
          >
            <Icon name="tag" size={14} /> Un PDF per categoria (tutto)
          </Button>
          <div style={{ height: 1, background: "var(--tcg-border)", margin: "4px 0" }} />
          {(labelCats ?? [])
            .filter((c) => c.product_count > 0)
            .map((c) => (
              <Button
                key={c.id}
                variant="secondary"
                block
                disabled={pdfBusy === "labels"}
                onClick={() => {
                  setLabelsOpen(false);
                  downloadPdf(
                    "labels",
                    "/api/products/qr-pdf/print",
                    `labels-${c.name.replace(/\s+/g, "-").toLowerCase()}.pdf`,
                    { category_id: c.id },
                  );
                }}
              >
                {c.name} · {c.product_count}
              </Button>
            ))}
        </div>
      </Modal>
    </>
  );
}

function SettingsGroup({
  title,
  children,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  footer?: string;
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: "var(--tcg-muted)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          padding: "18px 22px 8px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: "var(--tcg-card)",
          marginInline: 18,
          borderRadius: 16,
          border: "1px solid var(--tcg-border)",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
      {footer && (
        <div
          style={{
            fontSize: 10,
            color: "var(--tcg-muted)",
            padding: "8px 22px 0",
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

function statusLabel(value?: string): string {
  if (!value) return "—";
  if (value === "ok") return "Connesso · operativo";
  if (value === "configured") return "Configurato (non testato)";
  if (value === "not_configured") return "Non configurato";
  if (value === "expired") return "Token scaduto";
  if (value === "not_authorized") return "Da autorizzare";
  return value;
}

function statusTone(value?: string): "success" | "warn" | "ghost" | "danger" {
  if (value === "ok" || value === "configured") return "success";
  if (value === "expired" || value === "not_authorized") return "warn";
  if (value === "not_configured" || !value) return "ghost";
  return "danger";
}
